import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Clock, Flame } from "lucide-react";
import api from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1665332195309-9d75071138f0?crop=entropy&cs=srgb&fm=jpg&q=85";

const HIGHLIGHTS = [
  { name: "Jollof Rice", desc: "Smoky party jollof with your choice of protein", open: "Jollof Rice" },
  { name: "Fufu & Egusi", desc: "Rich soups with your choice of swallow", open: "Fufu & Soup" },
  { name: "Asun", desc: "Spicy diced, grilled goat meat cooked the right way", open: "Asun" },
];

export default function Home() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-stone-950">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-transparent" />
        <div className="relative mx-auto grid max-w-6xl gap-8 px-6 py-20 sm:py-28 lg:grid-cols-2 lg:items-center">
          <div>
            {settings?.ordering_paused && (
              <div className="mb-4 inline-block rounded-full border border-amber-400 bg-amber-100 px-4 py-1 text-sm font-semibold text-amber-900">
                Online ordering is paused — please check back soon
              </div>
            )}
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-orange-700/20 px-4 py-1.5 text-sm font-semibold text-amber-400">
              <Flame className="h-4 w-4" /> Authentic West African · Orlando, FL
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-tight text-white sm:text-6xl">
              Bold Nigerian flavors, <span className="text-amber-400">ready for pickup.</span>
            </h1>
            <p className="mt-5 max-w-md text-lg text-stone-300">
              From smoky party jollof to our famous Egusi and Fufu — order online, pay securely, and pick up hot.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/menu"
                data-testid="hero-order-button"
                className="inline-flex items-center gap-2 rounded-full bg-orange-700 px-7 py-3.5 font-bold text-white transition-colors duration-200 hover:bg-orange-800"
              >
                Order Pickup <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 rounded-full border border-stone-600 px-7 py-3.5 font-semibold text-stone-100 transition-colors duration-200 hover:bg-stone-800"
              >
                View Menu
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <img src="/logo.png" alt="Flavors Nigerian Restaurant" className="ml-auto h-96 w-full max-w-md rounded-3xl bg-stone-900 object-contain p-6 shadow-2xl ring-1 ring-amber-500/20" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="font-display text-3xl font-bold text-stone-900">Kitchen favorites</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {HIGHLIGHTS.map((h) => (
            <Link
              key={h.name}
              to={`/menu?open=${encodeURIComponent(h.open)}`}
              data-testid={`kitchen-favorite-${h.open.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group block rounded-2xl border border-stone-200 bg-white p-6 shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:border-orange-300 hover:shadow-md"
            >
              <Flame className="h-7 w-7 text-orange-700" />
              <h3 className="mt-4 font-display text-xl font-bold text-stone-900 group-hover:text-orange-700">{h.name}</h3>
              <p className="mt-2 text-sm text-stone-600">{h.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-orange-700">
                Order now <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="bg-stone-900 py-16 text-stone-100">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-amber-400">Our Story</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-stone-300">
            {settings?.story ||
              "Flavors Nigerian Restaurant brings the bold, soulful tastes of West Africa to Orlando, cooked with heat, heart, and heritage."}
          </p>
        </div>
      </section>

      {/* Info + Map */}
      <section className="mx-auto grid max-w-6xl gap-8 px-6 py-16 lg:grid-cols-2 lg:items-center">
        <div className="space-y-5">
          <h2 className="font-display text-3xl font-bold text-stone-900">Visit & Pickup</h2>
          <div className="flex items-start gap-3">
            <MapPin className="mt-1 h-5 w-5 text-orange-700" />
            <div><div className="font-semibold text-stone-900">Location</div><div className="text-stone-600">{settings?.address || "Orlando, FL"}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="mt-1 h-5 w-5 text-orange-700" />
            <div><div className="font-semibold text-stone-900">Phone</div><div className="text-stone-600">{settings?.phone || "(407) 000-0000"}</div></div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="mt-1 h-5 w-5 text-orange-700" />
            <div><div className="font-semibold text-stone-900">Hours</div><div className="text-stone-600">Mon–Sat · 11:00am – 7:00pm · Sun 1:00pm – 6:00pm</div></div>
          </div>
        </div>
        <div className="overflow-hidden rounded-3xl border border-stone-200 shadow-sm">
          <iframe
            title="map"
            src="https://www.google.com/maps?q=3530+S+Orange+Ave,+Orlando,+FL+32806&output=embed"
            className="h-72 w-full border-0"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
